import React, { useState } from 'react';
import { useDashboardScope } from '../../hooks/useDashboardScope';

// Manager/admin self-service filter on top of their site ceiling (set by an
// admin via ManagedSitesEditor). This is a display preference only — the
// server re-clamps site_ids to the ceiling, so this can never widen what the
// user can see. Renders nothing when the user has no ceiling (unrestricted
// tenant-wide access) since there is nothing meaningful to narrow.
const ScopeSwitcher: React.FC = () => {
  const { allowedSites, selected, setSelected, hasCeiling } = useDashboardScope();
  const [open, setOpen] = useState(false);

  if (!hasCeiling) {
    return null;
  }

  const label = selected.length === 0 ? 'Sites: All ▾' : `Sites: ${selected.length} ▾`;

  const toggleSite = (siteId: string) => {
    if (selected.includes(siteId)) {
      setSelected(selected.filter((id) => id !== siteId));
    } else {
      setSelected([...selected, siteId]);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        {label}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="absolute right-0 mt-2 w-64 max-h-72 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 z-20 p-3 space-y-2"
            role="region"
            aria-label="Site filter"
          >
            <div className="flex items-center justify-between pb-1 mb-1 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sites</span>
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected([])}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear (show all)
                </button>
              )}
            </div>
            {allowedSites.map((site) => (
              <label key={site.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  aria-label={site.name}
                  checked={selected.includes(site.id)}
                  onChange={() => toggleSite(site.id)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded-sm focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{site.name}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ScopeSwitcher;
